import { Flex, Typography } from "antd";
import { ExclamationCircleOutlined, ReloadOutlined } from "@ant-design/icons";
import type { EstadoDeCarga } from "../../hooks/use-dashboard-metrics";

const { Text } = Typography;

/**
 * Los estados que un widget puede tener sin datos que mostrar, y que **tienen que verse distinto**.
 *
 * Es lo que evita el peor resultado posible de este panel: un tablero de ceros que parece un
 * resultado. Un cero real ("no vendiste nada hoy") y un cero falso ("el cron todavía no corrió")
 * llevan a decisiones opuestas, y hasta acá los dos se dibujaban igual.
 */
export type WidgetStatus =
  | { tipo: "cargando" }
  | { tipo: "error"; mensaje: string; reintentar?: () => void }
  /** El cron nunca corrió: no hay con qué contestar todavía. */
  | { tipo: "sin-calcular" }
  /** Hay datos, pero ninguno cae en este recorte de período y filtros. */
  | { tipo: "vacio" }
  | { tipo: "ok" };

/**
 * Deriva el estado del widget a partir de la respuesta del endpoint.
 *
 * `hayDatos` lo decide cada widget, porque "vacío" significa algo distinto en cada uno: cero etapas
 * en el funnel, cero casos en el tiempo de cierre, cero operadores en la performance.
 */
export function estadoDeWidget(
  estado: EstadoDeCarga,
  error: string | null,
  nuncaCalculado: boolean | undefined,
  hayDatos: boolean,
  reintentar?: () => void,
): WidgetStatus {
  if (estado === "cargando") return { tipo: "cargando" };
  if (estado === "error")
    return { tipo: "error", mensaje: error ?? "Error al cargar", reintentar };
  if (nuncaCalculado) return { tipo: "sin-calcular" };
  if (!hayDatos) return { tipo: "vacio" };
  return { tipo: "ok" };
}

const MENSAJES: Record<string, { texto: string; color: string }> = {
  cargando: { texto: "Cargando…", color: "rgba(0,0,0,0.25)" },
  "sin-calcular": {
    texto: "Todavía sin calcular",
    color: "rgba(0,0,0,0.35)",
  },
  vacio: { texto: "Sin resultados para este recorte", color: "rgba(0,0,0,0.25)" },
};

/** Lo que se dibuja en lugar del contenido cuando el widget no tiene qué mostrar. */
export const WidgetPlaceholder = ({
  status,
  height,
}: {
  status: WidgetStatus;
  height?: number | string;
}) => {
  if (status.tipo === "ok") return null;

  if (status.tipo === "error") {
    return (
      <Flex
        vertical
        align="center"
        justify="center"
        gap={6}
        style={{ height: height ?? "100%", padding: 8 }}
      >
        <ExclamationCircleOutlined style={{ fontSize: 16, color: "#ff4d4f" }} />
        <Text style={{ fontSize: 11, color: "#ff4d4f", textAlign: "center" }}>
          No se pudo cargar
        </Text>
        {status.reintentar && (
          <a
            onClick={status.reintentar}
            style={{ fontSize: 11, display: "inline-flex", alignItems: "center", gap: 4 }}
          >
            <ReloadOutlined style={{ fontSize: 10 }} /> Reintentar
          </a>
        )}
      </Flex>
    );
  }

  const m = MENSAJES[status.tipo];
  return (
    <Flex align="center" justify="center" style={{ height: height ?? "100%", padding: 8 }}>
      <Text style={{ fontSize: 11, color: m.color, textAlign: "center" }}>{m.texto}</Text>
    </Flex>
  );
};

/**
 * El marcador de frescura: de cuándo son los números que se están mirando.
 *
 * Va siempre visible, no en un tooltip. El panel promete datos de hasta 15 minutos de antigüedad, y
 * una promesa que el usuario no puede verificar de un vistazo no sirve de nada.
 */
export const MarcadorDeFrescura = ({
  calculadoHasta,
  nuncaCalculado,
}: {
  calculadoHasta: string | null;
  nuncaCalculado: boolean;
}) => {
  if (nuncaCalculado || !calculadoHasta) {
    return (
      <Text type="secondary" style={{ fontSize: 11 }}>
        Métricas todavía sin calcular
      </Text>
    );
  }

  const min = Math.max(0, Math.round((Date.now() - new Date(calculadoHasta).getTime()) / 60_000));
  const texto =
    min < 1 ? "recién actualizado" : min === 1 ? "hace 1 minuto" : `hace ${min} minutos`;

  return (
    <Text type="secondary" style={{ fontSize: 11 }}>
      Datos actualizados {texto}
    </Text>
  );
};
